import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import PeerPageHeader from "./Components/PageHeader/PeerPageHeader";
import PeerEntitySelector from "./Components/EntitySelector/PeerEntitySelector";
import PeerSuggestionPanel from "./Components/PeerSuggestions/PeerSuggestionPanel";
import PeerEmptyState from "./Components/EmptyState/PeerEmptyState";
import FiltersRow from "../Analysis/Components/FiltersRow/FiltersRow";

import { Card, CardHeader, CardTitle, CardContent } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { getPeerSuggestions, getAnalysisRecords } from "../../Services/Analysis";
import { getInstitutionDetailSummaryByID } from "Explore/Services/Institution";
import useAdminInstitution from "Explore/Hooks/useAdminInstitution";

const Peer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const urlEntityId = searchParams.get("entity_id") || location.state?.entityId;
  const urlYearRange = searchParams.get("year_range") || location.state?.filters?.yearRange || "last_10_years";
  const urlSubjectArea = searchParams.get("subject_area") ? searchParams.get("subject_area").split(",") : (location.state?.filters?.subjectArea || []);
  const urlState = searchParams.get("state") ? searchParams.get("state").split(",") : (location.state?.filters?.state || []);
  const urlKeywordsOperator = searchParams.get("keywords_operator") || location.state?.filters?.keywordsOperator || "or";
  const user = useSelector((state) => state.user?.data);
  const { institution: adminInstitution, isLoading: isSummaryLoading } = useAdminInstitution();
  const [baseEntity, setBaseEntity] = useState(null); // The entity we are finding peers for
  const [hasInitialized, setHasInitialized] = useState(false);
  
  const [suggestedPeers, setSuggestedPeers] = useState([]);
  const [selectedPeers, setSelectedPeers] = useState([]);
  
  const [filters, setFilters] = useState(location.state?.filters || {
    yearRange: urlYearRange,
    subjectArea: urlSubjectArea,
    state: urlState,
    keywordsOperator: urlKeywordsOperator,
    geography: "us",
    metric: "publications",
    collaboratorType: "institution",
  });

  const [isLoadingPeers, setIsLoadingPeers] = useState(false);

  useEffect(() => {
    // Wait until user data is available and not loading summary
    if (user && !isSummaryLoading && !hasInitialized) {
      const clearUrlParams = () => {
        if (Array.from(searchParams.keys()).length > 0) {
          navigate(location.pathname, { replace: true, state: location.state });
        }
      };

      if (location.state?.entity) {
        const incomingEntity = location.state.entity;
        setBaseEntity(incomingEntity);
        fetchPeers(incomingEntity, filters);
        clearUrlParams();
        setHasInitialized(true);
      } else if (urlEntityId) {
        getInstitutionDetailSummaryByID(urlEntityId, (data) => {
          const fullEntity = {
            ...data,
            id: urlEntityId,
            type: "institution",
            name: data?.name,
            avatar: data?.logo_url || data?.photo_url || `https://media.academic.online/institutions_images/${urlEntityId}/seal.png`,
            photo: data?.logo_url || data?.photo_url || `https://media.academic.online/institutions_images/${urlEntityId}/seal.png`
          };
          setBaseEntity(fullEntity);
          fetchPeers(fullEntity, filters);
          clearUrlParams();
          setHasInitialized(true);
        });
      } else if (adminInstitution) {
        setBaseEntity(adminInstitution);
        fetchPeers(adminInstitution, filters);
        clearUrlParams();
        setHasInitialized(true);
      } else {
        clearUrlParams();
        setHasInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, adminInstitution, isSummaryLoading, hasInitialized]);

  const fetchPeers = async (entity, currentFilters) => {
    if (!entity) return;
    setIsLoadingPeers(true);
    try {
      // 1. Fetch peer suggestions
      const peerData = await getPeerSuggestions({
        institutionId: entity.id,
        yearRange: currentFilters.yearRange,
        subjectArea: currentFilters.subjectArea,
        keywordsOperator: currentFilters.keywordsOperator,
        state: currentFilters.state,
      });

      let suggested = [];
      if (peerData && peerData.peers) {
        suggested = peerData.peers;
      }
      
      
      // Update UI immediately with basic peers so user doesn't wait
      setSuggestedPeers(suggested);
      setSelectedPeers(suggested);
      setIsLoadingPeers(false);

      // 2. Fetch full analysis records for base entity + all peers in the background
      const entityIds = [entity.id, ...suggested.map(p => p.id)];
      getAnalysisRecords({
        entityType: "institution",
        entityIds,
        yearRange: currentFilters.yearRange,
        subjectArea: currentFilters.subjectArea,
        keywordsOperator: currentFilters.keywordsOperator,
        state: currentFilters.state,
        pageSize: entityIds.length || 20,
      }).then(analysisData => {
        if (analysisData && analysisData.records) {
          const recordsMap = {};
          analysisData.records.forEach(r => { recordsMap[r.id] = r; });

          if (recordsMap[entity.id]) {
            setBaseEntity(prev => ({ ...prev, ...recordsMap[entity.id] }));
          }

          setSuggestedPeers(prev => prev.map(p => ({
            ...p,
            ...(recordsMap[p.id] || {})
          })));
          
          setSelectedPeers(prev => prev.map(p => ({
            ...p,
            ...(recordsMap[p.id] || {})
          })));
        }
      }).catch(err => console.error("Failed to fetch background analysis:", err));

    } catch (error) {
      console.error("Failed to fetch peers:", error);
      setIsLoadingPeers(false);
    }
  };

  const handleEntitySelect = (entity) => {
    setBaseEntity(entity);
    fetchPeers(entity, filters);
  };

  const handleEntityClear = () => {
    setBaseEntity(null);
    setSuggestedPeers([]);
    setSelectedPeers([]);
  };

  const handleTogglePeer = (peer) => {
    setSelectedPeers(prev => {
      const isSelected = prev.find(p => p.id === peer.id);
      if (isSelected) {
        return prev.filter(p => p.id !== peer.id);
      } else {
        return [...prev, peer];
      }
    });
  };

  const handleToggleSelectAll = (selectAll) => {
    if (selectAll) {
      setSelectedPeers([...suggestedPeers]);
    } else {
      setSelectedPeers([]);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50">
      <div className="flex flex-col flex-1 p-6 gap-6">
        
        <PeerPageHeader onSave={() => {}} onExport={() => {}} />

        {(!hasInitialized || isSummaryLoading) ? (
          <div className="flex flex-col gap-6 w-full flex-1">
            {/* Entity Selector Skeleton */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <Skeleton className="h-4 w-32 mb-4" />
              <Skeleton className="h-12 w-full" />
            </div>
            
            {/* Main Area Skeleton */}
            <div className="flex flex-col lg:flex-row gap-6 w-full flex-1">
              {/* Sidebar Skeleton */}
              <div className="w-full lg:w-[280px] bg-white border border-slate-200 rounded-lg p-6 h-max shadow-sm">
                <Skeleton className="h-6 w-1/2 mb-8" />
                <Skeleton className="h-10 w-full mb-6" />
                <Skeleton className="h-10 w-full mb-6" />
                <Skeleton className="h-10 w-full mb-6" />
                <Skeleton className="h-10 w-full" />
              </div>
              {/* Content Skeleton */}
              <div className="flex-1 flex flex-col gap-6">
                <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                  <Skeleton className="h-8 w-1/3 mb-6" />
                  <Skeleton className="h-32 w-full mb-4" />
                  <Skeleton className="h-32 w-full" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* 1. Entity Selector Section */}
            <PeerEntitySelector
              selectedEntity={baseEntity}
              onSelect={handleEntitySelect}
              onClear={handleEntityClear}
            />

            {/* 2. Main Layout Area (Sidebar + Content) */}
            {!baseEntity ? (
              <PeerEmptyState />
            ) : (
              <div className="flex flex-col lg:flex-row items-start flex-1 gap-6 w-full h-full">
                {/* Left Sidebar: Filters */}
                <div className="w-full lg:w-[280px] flex-shrink-0 lg:sticky lg:top-0 h-max max-h-screen overflow-y-auto bg-white border border-slate-200 shadow-sm rounded-lg">
                  <FiltersRow 
                    filters={filters} 
                    onFilterChange={setFilters} 
                    entityType="institution"
                    onRunAnalysis={(newFilters) => {
                      setFilters(newFilters);
                      if (baseEntity) {
                        fetchPeers(baseEntity, newFilters);
                      }
                    }}
                  />
                </div>

                {/* Right side content */}
                <div className="flex-1 min-w-0 flex flex-col gap-8 w-full h-full">
                  <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    
                    <PeerSuggestionPanel
                      baseEntity={baseEntity}
                      suggestedPeers={suggestedPeers}
                      selectedPeers={selectedPeers}
                      onTogglePeer={handleTogglePeer}
                      onToggleSelectAll={handleToggleSelectAll}
                      isLoading={isLoadingPeers}
                      filters={filters}
                    />

                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Peer;
